import {
  CaretLeft,
  Gift,
  ShareNetwork,
  UserPlus,
  Coins,
  Users,
  CheckCircle,
  Airplane,
  SealCheck,
  Ticket,
  Icon,
} from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { HashLoader } from "react-spinners";
import ReferralInviteCard from "../components/ReferralInviteCard";
import { useReferralSummary } from "../hooks/useReferralSummary";
import {
  useReferralDownlines,
  ReferralDownline,
} from "../hooks/useReferralDownlines";
import {
  useReferralRewards,
  ReferralReward,
} from "../hooks/useReferralRewards";

const STEPS = [
  {
    icon: ShareNetwork,
    title: "Share your code or link",
    description:
      "Send your unique referral code or invite link to friends and family.",
  },
  {
    icon: UserPlus,
    title: "They sign up with it",
    description:
      "Your friend creates a Visaro account using your code and completes setup.",
  },
  {
    icon: Coins,
    title: "Rewards apply",
    description:
      "Once your friend reaches KYC tier 3 and books a flight, you both get rewarded.",
  },
];

const naira = (n: number) => `₦${Number(n || 0).toLocaleString()}`;

const Referrals = () => {
  const navigate = useNavigate();

  return (
    <div className="relative min-h-[70vh] px-5 py-8">
      <div className="mx-auto max-w-6xl flex flex-col gap-6">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="p-2 text-center text-deepBlue border px-6 rounded-xl font-medium flex gap-2 items-center border-gray-300 self-start hover:bg-gray-50 transition"
        >
          <CaretLeft weight="bold" />
          <span>Back</span>
        </button>

        {/* Hero */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-deepBlue via-deepBlue to-[#102B5C] text-white p-8 md:p-10">
          <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-white/10" />
          <div className="absolute right-16 bottom-[-5rem] w-48 h-48 rounded-full bg-visaro-orange/20" />
          <div className="relative z-10 flex flex-col gap-4 max-w-xl">
            <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center">
              <Gift size={28} weight="duotone" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold">
              Share Visaro, earn rewards
            </h2>
            <p className="text-white/75">
              Invite your friends to Visaro with your personal code or link.
              When they join, verify to tier 3 and book a flight, you both get
              rewarded.
            </p>
          </div>
        </div>

        {/* Two-column: wider tracking column + sticky invite card */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left — progress, downlines, rewards, how it works */}
          <div className="lg:col-span-2 order-2 lg:order-1 flex flex-col gap-6">
            <ProgressSection />
            <DownlinesSection />
            <RewardsSection />

            {/* How it works */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm flex flex-col gap-5">
              <h3 className="font-bold text-lg text-gray-900">How it works</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {STEPS.map((step, index) => (
                  <div key={step.title} className="flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-visaro-orange/10 text-visaro-orange flex items-center justify-center shrink-0">
                        <step.icon size={20} weight="bold" />
                      </div>
                      <span className="text-sm font-bold text-gray-400">
                        Step {index + 1}
                      </span>
                    </div>
                    <div>
                      <div className="font-semibold text-deepBlue">
                        {step.title}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        {step.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right — sticky invite card */}
          <aside className="order-1 lg:order-2">
            <div className="lg:sticky lg:top-6 flex flex-col gap-4">
              <ReferralInviteCard />
              <p className="text-xs text-gray-400 text-center">
                Referral rewards are subject to Visaro's referral terms and
                eligibility rules.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------- Progress ------------------------------- */

function ProgressSection() {
  const { data, isLoading } = useReferralSummary();
  const summary = data?.data;
  const counts = summary?.counts;
  const earnings = summary?.earnings;

  if (isLoading || (!counts && !earnings)) return null;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm flex flex-col gap-5">
      <h3 className="font-bold text-lg text-gray-900">Your progress</h3>
      <div className="grid grid-cols-3 gap-3">
        <Stat icon={Users} label="Invited" value={counts?.downlines ?? 0} />
        <Stat icon={Airplane} label="Booked a flight" value={counts?.qualified ?? 0} />
        <Stat icon={SealCheck} label="Rewarded" value={counts?.rewarded ?? 0} />
      </div>
      {earnings && (
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl bg-emerald-50 border border-emerald-100 p-4">
            <p className="text-xs font-semibold text-emerald-700">Total earned</p>
            <p className="text-xl font-bold text-emerald-900 mt-1">
              {naira(earnings.paid)}
            </p>
          </div>
          <div className="rounded-xl bg-amber-50 border border-amber-100 p-4">
            <p className="text-xs font-semibold text-amber-700">Pending</p>
            <p className="text-xl font-bold text-amber-900 mt-1">
              {naira(earnings.pending)}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({
  icon: IconCmp,
  label,
  value,
}: {
  icon: Icon;
  label: string;
  value: number;
}) {
  return (
    <div className="rounded-xl bg-gray-50 border border-gray-100 p-4 flex flex-col items-center text-center gap-1.5">
      <div className="w-9 h-9 rounded-full bg-deepBlue/5 text-deepBlue flex items-center justify-center">
        <IconCmp size={18} weight="duotone" />
      </div>
      <p className="text-xl font-bold text-deepBlue">{value}</p>
      <p className="text-[11px] font-medium text-gray-500 leading-tight">{label}</p>
    </div>
  );
}

/* ------------------------------- Downlines ------------------------------ */

function DownlinesSection() {
  const { data, isLoading } = useReferralDownlines();
  const items = data?.data?.items ?? [];

  if (isLoading) {
    return (
      <SectionShell title="Your downlines">
        <div className="flex items-center justify-center gap-3 py-6">
          <HashLoader size={22} color="#EA5C29" />
          <span className="text-gray-600 text-sm">Loading…</span>
        </div>
      </SectionShell>
    );
  }

  if (items.length === 0) {
    return (
      <SectionShell title="Your downlines">
        <p className="text-sm text-gray-500 py-4">
          No one has signed up with your code yet. Share it to start earning.
        </p>
      </SectionShell>
    );
  }

  return (
    <SectionShell title="Your downlines" count={data?.data?.total}>
      <ul className="flex flex-col divide-y divide-gray-100">
        {items.map((d, i) => (
          <DownlineRow key={i} downline={d} />
        ))}
      </ul>
    </SectionShell>
  );
}

function DownlineRow({ downline }: { downline: ReferralDownline }) {
  const rewarded = downline.rewardStatus !== "NONE";
  const steps = [
    { label: "Joined", done: true },
    { label: "Tier 3", done: downline.kycTier >= 3 },
    { label: "Flight", done: downline.hasAviationTxn },
    { label: "Rewarded", done: rewarded },
  ];

  return (
    <li className="py-3 flex items-center justify-between gap-4">
      <div className="min-w-0">
        <p className="font-semibold text-deepBlue text-sm truncate">
          {downline.maskedName}
        </p>
        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
          {steps.map((s) => (
            <span
              key={s.label}
              className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${
                s.done
                  ? "bg-emerald-50 text-emerald-700"
                  : "bg-gray-100 text-gray-400"
              }`}
            >
              {s.done && <CheckCircle size={12} weight="fill" />}
              {s.label}
            </span>
          ))}
        </div>
      </div>
      {rewarded ? (
        <span className="text-xs font-semibold text-emerald-600 shrink-0">
          Rewarded
        </span>
      ) : (
        <span className="text-xs text-gray-400 shrink-0">In progress</span>
      )}
    </li>
  );
}

/* -------------------------------- Rewards ------------------------------- */

function RewardsSection() {
  const navigate = useNavigate();
  const { data, isLoading } = useReferralRewards();
  const items = data?.data?.items ?? [];

  if (isLoading || items.length === 0) return null;

  return (
    <SectionShell title="Your rewards">
      <ul className="flex flex-col divide-y divide-gray-100">
        {items.map((r) => (
          <RewardRow
            key={r.id}
            reward={r}
            onClaim={() => navigate("/payments/bills/referral-rewards")}
          />
        ))}
      </ul>
    </SectionShell>
  );
}

function RewardRow({
  reward,
  onClaim,
}: {
  reward: ReferralReward;
  onClaim: () => void;
}) {
  const isVoucher = reward.rewardType === "VOUCHER";
  const kind = reward.rewardMeta?.voucherKind;
  const title = isVoucher
    ? `${kind === "AIRTIME" ? "Airtime" : "Data"} voucher${
        reward.rewardMeta?.planName ? ` · ${reward.rewardMeta.planName}` : ""
      }`
    : "Cash reward";

  return (
    <li className="py-3 flex items-center justify-between gap-4">
      <div className="flex items-start gap-3 min-w-0">
        <div className="p-2 rounded-xl bg-orange-50 text-visaro-orange shrink-0">
          {isVoucher ? (
            <Ticket size={20} weight="duotone" />
          ) : (
            <Coins size={20} weight="duotone" />
          )}
        </div>
        <div className="min-w-0">
          <p className="font-semibold text-deepBlue text-sm">{title}</p>
          <p className="text-xs text-gray-500 mt-0.5">
            {reward.amount != null ? naira(reward.amount) : ""}
            {reward.role === "REFERRER" ? " · from a referral" : " · welcome gift"}
          </p>
        </div>
      </div>
      <div className="shrink-0 text-right">
        {reward.claimable ? (
          <button
            type="button"
            onClick={onClaim}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-visaro-orange text-white text-xs font-semibold hover:bg-visaro-orange/90 transition-colors"
          >
            Claim
          </button>
        ) : (
          <StatusPill status={reward.status} />
        )}
      </div>
    </li>
  );
}

function StatusPill({ status }: { status: string }) {
  const done = status === "FULFILLED" || status === "CLAIMED";
  return (
    <span
      className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${
        done
          ? "bg-emerald-50 text-emerald-700"
          : "bg-gray-100 text-gray-500"
      }`}
    >
      {done ? "Received" : status.charAt(0) + status.slice(1).toLowerCase()}
    </span>
  );
}

/* -------------------------------- Shared -------------------------------- */

function SectionShell({
  title,
  count,
  children,
}: {
  title: string;
  count?: number;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-lg text-gray-900">{title}</h3>
        {typeof count === "number" && count > 0 && (
          <span className="text-xs font-semibold text-gray-400">{count} total</span>
        )}
      </div>
      {children}
    </div>
  );
}

export default Referrals;
